/**
 * Psychological Flexibility Diagnostic Page (v2.0)
 *
 * ACT-based wellbeing diagnostic using 10 psychological inflexibility measures.
 * Standalone assessment (no persona, no transformation value).
 *
 * v2.0 Features:
 * - "How often?" question framing (all measure inflexibility)
 * - Psychological Inflexibility Factor calculation
 * - Wellbeing burden indicator
 * - No transformation value calculations (wellbeing only)
 */

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Footer from '../components/Footer';
import {
  calculatePsychologicalFlexibilityV2
} from '../utils/algorithms';

function PsychologicalFlexibilityDiagnostic() {
  const [searchParams] = useSearchParams();
  const [validationState, setValidationState] = useState('loading');
  const [guidCode, setGuidCode] = useState(null);
  const [showReport, setShowReport] = useState(false);

  const [leaderInfo, setLeaderInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: ''
  });

  const [flexScores, setFlexScores] = useState({
    flex1: 5, flex2: 5, flex3: 5, flex4: 5, flex5: 5,
    flex6: 5, flex7: 5, flex8: 5, flex9: 5, flex10: 5
  });

  const [results, setResults] = useState(null);

  // 10 inflexibility measures with "How often?" framing (all measure INFLEXIBILITY)
  const flexibilityMeasures = [
    { id: 1, name: 'Mind Wandering', question: 'How often does your mind drift away from what you\'re doing?' },
    { id: 2, name: 'Thought Entanglement', question: 'How often do you get caught up in unhelpful thoughts or worries?' },
    { id: 3, name: 'Emotional Avoidance', question: 'How often do you try to avoid or suppress difficult emotions?' },
    { id: 4, name: 'Self-Criticism', question: 'How often do you judge yourself harshly or get caught in self-critical stories?' },
    { id: 5, name: 'Values Disconnection', question: 'How often do you feel unclear about what truly matters to you?' },
    { id: 6, name: 'Inaction', question: 'How often do you avoid taking action on things that matter because they feel hard?' },
    { id: 7, name: 'Rigidity', question: 'How often do you feel stuck in rigid patterns or unable to adapt?' },
    { id: 8, name: 'Experiential Avoidance', question: 'How often do you avoid situations that might bring up uncomfortable feelings?' },
    { id: 9, name: 'Cognitive Fusion', question: 'How often do you treat your thoughts as absolute truths rather than just thoughts?' },
    { id: 10, name: 'Behavioral Inflexibility', question: 'How often do you persist with unhelpful behaviors even when they don\'t work?' }
  ];

  // Validate GUID on component mount
  useEffect(() => {
    const code = searchParams.get('code');

    if (!code) {
      setValidationState('invalid');
      return;
    }

    const validateCode = async () => {
      try {
        const response = await fetch(`/api/validate-code?code=${code}`);
        const data = await response.json();

        if (data.success && data.valid) {
          setGuidCode(code);

          if (data.completed) {
            setValidationState('completed');
          } else {
            setValidationState('valid');
            setLeaderInfo({
              firstName: data.leader?.firstName || '',
              lastName: data.leader?.lastName || '',
              email: data.leader?.email || '',
              role: ''
            });
          }
        } else {
          setValidationState('invalid');
        }
      } catch (error) {
        console.error('GUID validation error:', error);
        setValidationState('invalid');
      }
    };

    validateCode();
  }, [searchParams]);

  const handleSliderChange = (flexId, value) => {
    setFlexScores(prev => ({
      ...prev,
      [`flex${flexId}`]: parseInt(value)
    }));
  };

  const handleSubmit = async () => {
    // Calculate results using v2.0 algorithm
    const calculations = calculatePsychologicalFlexibilityV2(flexScores);

    // Get top 3 highest inflexibility areas
    const scorePairs = flexibilityMeasures.map(measure => ({
      ...measure,
      score: flexScores[`flex${measure.id}`]
    }));
    const topInflexibilities = scorePairs.sort((a, b) => b.score - a.score).slice(0, 3);

    setResults({ calculations, topInflexibilities });

    // Save results to database
    if (guidCode) {
      try {
        await fetch('/api/save-psychological-flexibility-results', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            code: guidCode,
            flexScores,
            calculations
          })
        });
      } catch (error) {
        console.error('Failed to save results:', error);
        // Continue showing results even if save fails
      }
    }

    setShowReport(true);
    window.scrollTo(0, 0);
  };

  const handleStartOver = () => {
    setShowReport(false);
    setFlexScores({
      flex1: 5, flex2: 5, flex3: 5, flex4: 5, flex5: 5,
      flex6: 5, flex7: 5, flex8: 5, flex9: 5, flex10: 5
    });
    setResults(null);
    window.scrollTo(0, 0);
  };

  // Loading state
  if (validationState === 'loading') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-daring-navy mx-auto mb-4"></div>
          <p className="text-daring-grey">Validating your diagnostic link...</p>
        </div>
      </div>
    );
  }

  // Invalid state
  if (validationState === 'invalid') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-red-500 text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold text-daring-navy mb-4">
            Invalid or Expired Link
          </h1>
          <p className="text-daring-grey mb-6">
            This diagnostic link is invalid, has expired, or has already been used.
            Please request a new diagnostic link.
          </p>
          <a
            href="/request-diagnostic"
            className="inline-block bg-daring-teal hover:bg-daring-navy text-white font-bold px-6 py-3 rounded transition-all"
          >
            Request New Diagnostic
          </a>
        </div>
      </div>
    );
  }

  // Completed state
  if (validationState === 'completed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-lg shadow-xl p-8 max-w-md w-full text-center">
          <div className="text-green-500 text-5xl mb-4">✅</div>
          <h1 className="text-2xl font-bold text-daring-navy mb-4">
            Diagnostic Already Completed
          </h1>
          <p className="text-daring-grey mb-6">
            You've already completed this diagnostic. Your results have been saved
            and sent to your email.
          </p>
          <a
            href="/request-diagnostic"
            className="inline-block bg-daring-teal hover:bg-daring-navy text-white font-bold px-6 py-3 rounded transition-all"
          >
            Request New Diagnostic
          </a>
        </div>
      </div>
    );
  }

  // Input form
  if (!showReport) {
    return (
      <div className="min-h-screen bg-gray-50 py-12 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h1 className="text-3xl font-bold text-daring-navy mb-4">
              Psychological Flexibility Diagnostic
            </h1>
            <p className="text-daring-grey mb-4">
              Welcome, <strong>{leaderInfo.firstName} {leaderInfo.lastName}</strong>
            </p>
            <p className="text-daring-grey mb-4">
              This wellbeing diagnostic assesses 10 patterns of psychological inflexibility.
              Rate how often you experience each pattern from 0 (constantly) to 10 (never).
            </p>
            <div className="bg-blue-50 border-l-4 border-daring-teal p-4 rounded">
              <p className="text-sm text-daring-navy">
                <strong>How to interpret:</strong> Higher scores (experiencing inflexibility patterns rarely) indicate better psychological flexibility
              </p>
            </div>
          </div>

          {/* Flexibility Measure Sliders */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8">
            <h2 className="text-2xl font-bold text-daring-navy mb-6">Rate Each Pattern</h2>

            <div className="space-y-8">
              {flexibilityMeasures.map((measure) => (
                <div key={measure.id} className="border-b border-gray-200 pb-6 last:border-0">
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1">
                      <h3 className="font-bold text-daring-navy text-lg">
                        {measure.id}. {measure.name}
                      </h3>
                      <p className="text-sm text-daring-grey mt-1">{measure.question}</p>
                    </div>
                    <div className="ml-4 text-right">
                      <span className="text-3xl font-bold text-daring-teal">
                        {flexScores[`flex${measure.id}`]}
                      </span>
                      <span className="text-sm text-daring-grey block">/10</span>
                    </div>
                  </div>

                  <input
                    type="range"
                    min="0"
                    max="10"
                    value={flexScores[`flex${measure.id}`]}
                    onChange={(e) => handleSliderChange(measure.id, e.target.value)}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-daring-teal"
                  />

                  <div className="flex justify-between text-xs text-daring-grey mt-1">
                    <span>0 - Constantly</span>
                    <span>5 - Sometimes</span>
                    <span>10 - Never</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="bg-white rounded-lg shadow-md p-8">
            <button
              onClick={handleSubmit}
              className="w-full bg-daring-teal hover:bg-daring-navy text-white font-bold py-4 px-6 rounded text-lg transition-all"
            >
              Generate Results
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Results page
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Overall Results */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h1 className="text-3xl font-bold text-daring-navy mb-6">
            Your Psychological Inflexibility Assessment
          </h1>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="text-4xl font-bold text-daring-teal mb-2">
                {(results.calculations.psychologicalInflexibilityFactor * 100).toFixed(1)}%
              </div>
              <div className="text-sm text-daring-grey">Psychological Inflexibility Factor</div>
              <div className="text-xs text-daring-grey mt-1">
                (Average of 10 inflexibility patterns)
              </div>
            </div>

            <div className="text-center p-6 bg-gray-50 rounded-lg">
              <div className="text-4xl font-bold mb-2"
                   style={{
                     color: results.calculations.burdenLevel === 'Critical' ? '#991b1b' :
                            results.calculations.burdenLevel === 'High' ? '#ef4444' :
                            results.calculations.burdenLevel === 'Moderate' ? '#f59e0b' : '#10b981'
                   }}>
                {results.calculations.burdenLevel}
              </div>
              <div className="text-sm text-daring-grey">Wellbeing Risk Level</div>
              <div className="text-xs text-daring-grey mt-1">
                (Wellbeing Burden: {results.calculations.averageScore.toFixed(1)}/10)
              </div>
            </div>
          </div>

          <div className="bg-blue-50 border-l-4 border-daring-teal p-6 rounded mb-6">
            <p className="text-daring-navy">
              <strong>Assessment:</strong> {results.calculations.burdenDescription}
            </p>
          </div>

          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded">
            <p className="text-sm text-daring-navy">
              <strong>About This Diagnostic:</strong> This is a standalone wellbeing assessment that measures psychological inflexibility patterns.
              Unlike the System Burden or Leadership Burden diagnostics, this does not calculate transformation value at risk.
              Instead, it provides insights into your mental wellbeing and ACT-based intervention opportunities.
            </p>
          </div>
        </div>

        {/* Top 3 Inflexibility Patterns */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-daring-navy mb-6">
            Top 3 Inflexibility Patterns to Address
          </h2>

          <div className="space-y-6">
            {results.topInflexibilities.map((pattern, index) => (
              <div key={pattern.id} className="border-l-4 border-orange-500 pl-6 py-4">
                <div className="flex items-start justify-between mb-2">
                  <h3 className="text-xl font-bold text-daring-navy">
                    {index + 1}. {pattern.name}
                  </h3>
                  <span className="text-2xl font-bold text-orange-600 ml-4">
                    {pattern.score}/10
                  </span>
                </div>

                <p className="text-daring-grey mb-3">
                  {pattern.question}
                </p>

                <div className="bg-green-50 p-4 rounded">
                  <p className="text-sm text-daring-navy">
                    <strong>ACT Intervention:</strong> Work with a coach on developing psychological flexibility skills to address this pattern.
                    ACT coaching has shown 8% effectiveness improvement (Skews et al. 2018).
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recommendations */}
        <div className="bg-white rounded-lg shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-daring-navy mb-6">
            General ACT Practices
          </h2>

          <ul className="space-y-3">
            <li className="flex items-start">
              <span className="text-daring-teal mr-3 mt-1">•</span>
              <span className="text-daring-grey"><strong>Present Moment Awareness:</strong> Practice mindfulness to notice when your mind wanders and gently bring it back</span>
            </li>
            <li className="flex items-start">
              <span className="text-daring-teal mr-3 mt-1">•</span>
              <span className="text-daring-grey"><strong>Cognitive Defusion:</strong> Learn to see thoughts as just thoughts, not absolute truths</span>
            </li>
            <li className="flex items-start">
              <span className="text-daring-teal mr-3 mt-1">•</span>
              <span className="text-daring-grey"><strong>Acceptance:</strong> Allow difficult emotions to be present without trying to suppress or avoid them</span>
            </li>
            <li className="flex items-start">
              <span className="text-daring-teal mr-3 mt-1">•</span>
              <span className="text-daring-grey"><strong>Values Clarity:</strong> Identify what truly matters to you and let values guide your actions</span>
            </li>
            <li className="flex items-start">
              <span className="text-daring-teal mr-3 mt-1">•</span>
              <span className="text-daring-grey"><strong>Committed Action:</strong> Take small steps toward what matters, even when it feels uncomfortable</span>
            </li>
          </ul>
        </div>

        {/* Actions */}
        <div className="bg-white rounded-lg shadow-md p-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={() => window.print()}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-daring-navy font-bold py-3 px-6 rounded transition-all"
            >
              Print Results
            </button>

            <button
              onClick={handleStartOver}
              className="flex-1 bg-daring-teal hover:bg-daring-navy text-white font-bold py-3 px-6 rounded transition-all"
            >
              Start Over
            </button>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default PsychologicalFlexibilityDiagnostic;
